import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login, register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') === 'register' ? 'register' : 'login';

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [regData, setRegData] = useState({ name: '', email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      await googleLogin(credentialResponse.credential);
      toast.success('Welcome!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(regData.email, regData.password, regData.name);
      toast.success('Account created!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="login-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/7248774/pexels-photo-7248774.jpeg"
          alt="Technology"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20" />
        <div className="absolute bottom-0 left-0 p-12">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center shadow-lg">
              <span className="text-white font-heading font-bold text-lg">V2</span>
            </div>
            <span className="font-heading font-bold text-2xl text-white">
              vm2pod
            </span>
          </Link>
          <h2 className="font-heading text-3xl font-bold text-white mb-3">
            From VMs to Pods. Expert Knowledge.
          </h2>
          <p className="text-base text-white/60 max-w-md">
            Join a community of expert authors sharing insights on VMware, Kubernetes, DevOps, and cloud-native technologies.
          </p>
        </div>
      </div>

      {/* Right panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>

          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-8">
              <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Create Account</TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login">
              <div className="mb-8">
                <h1 className="font-heading text-2xl font-bold tracking-tight">Welcome back</h1>
                <p className="text-sm text-muted-foreground mt-1">Sign in to your account to continue</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <Label htmlFor="login-email" className="text-sm font-medium">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="you@example.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    className="h-11 mt-1.5"
                    required
                    data-testid="login-email"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-sm font-medium">Password</Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="login-password"
                      type={showPass ? "text" : "password"}
                      placeholder="Enter your password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      className="h-11 pr-10"
                      required
                      data-testid="login-password"
                    />
                    <button 
                      type="button" 
                      onClick={() => setShowPass(!showPass)} 
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 btn-primary btn-press font-medium" 
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
                
                <div className="text-center">
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">
                    Forgot your password?
                  </Link>
                </div>
              </form>

              {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
                <div className="mt-6">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                    <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">Or continue with</span></div>
                  </div>
                  <div className="mt-4 flex justify-center" data-testid="google-login-btn">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => toast.error('Google sign-in failed')}
                      text="signin_with"
                      shape="pill"
                      width="300"
                    />
                  </div>
                </div>
              )}

              <p className="text-center text-sm text-muted-foreground mt-6">
                Don't have an account?{' '}
                <button 
                  onClick={() => document.querySelector('[value="register"]')?.click()} 
                  className="text-primary font-medium hover:underline"
                >
                  Create one
                </button>
              </p>
            </TabsContent>

            {/* Register Tab */}
            <TabsContent value="register">
              <div className="mb-8">
                <h1 className="font-heading text-2xl font-bold tracking-tight">Create an account</h1>
                <p className="text-sm text-muted-foreground mt-1">Join vm2pod and start reading expert content</p>
              </div>

              <form onSubmit={handleRegister} className="space-y-5">
                <div>
                  <Label htmlFor="reg-name" className="text-sm font-medium">Full Name</Label>
                  <Input
                    id="reg-name"
                    type="text"
                    placeholder="John Doe"
                    value={regData.name}
                    onChange={(e) => setRegData({...regData, name: e.target.value})}
                    className="h-11 mt-1.5"
                    required
                    data-testid="register-name"
                  />
                </div>
                <div>
                  <Label htmlFor="reg-email" className="text-sm font-medium">Email</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    placeholder="you@example.com"
                    value={regData.email}
                    onChange={(e) => setRegData({...regData, email: e.target.value})}
                    className="h-11 mt-1.5"
                    required
                    data-testid="register-email"
                  />
                </div>
                <div>
                  <Label htmlFor="reg-password" className="text-sm font-medium">Password</Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="reg-password"
                      type={showPass ? "text" : "password"}
                      placeholder="Create a password"
                      value={regData.password}
                      onChange={(e) => setRegData({...regData, password: e.target.value})}
                      className="h-11 pr-10"
                      required
                      minLength={6}
                      data-testid="register-password"
                    />
                    <button 
                      type="button" 
                      onClick={() => setShowPass(!showPass)} 
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">Must be at least 6 characters</p>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 btn-primary btn-press font-medium" 
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {loading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>

              {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
                <div className="mt-6">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                    <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">Or continue with</span></div>
                  </div>
                  <div className="mt-4 flex justify-center" data-testid="google-register-btn">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => toast.error('Google sign-up failed')}
                      text="signup_with"
                      shape="pill"
                      width="300"
                    />
                  </div>
                </div>
              )}

              <p className="text-center text-sm text-muted-foreground mt-6">
                Already have an account?{' '}
                <button 
                  onClick={() => document.querySelector('[value="login"]')?.click()} 
                  className="text-primary font-medium hover:underline"
                >
                  Sign in
                </button>
              </p>
            </TabsContent>
          </Tabs>

          <p className="text-center text-xs text-muted-foreground mt-8">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}
